import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Chip,
    OutlinedInput,
    List,
    ListItemButton,
    ListItemText,
    Grid,
    Card,
    CardMedia,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';

import { fetchKnowledgeByClassifiers, fetchAllClassifiers } from '../api/knowledgeApi';
import { fetchAllImages } from '../api/archivesApi';
import { getApi } from '../api/axiosInstance';

// Remove /api from the base URL for static assets
const STATIC_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

const getImageUrl = (filepath) => {
    return `${STATIC_BASE_URL}${filepath}`;
};

const AddPromptModal = ({ open, onClose, onSuccess, existingPrompt }) => {
    const [userPrompt, setUserPrompt] = useState('');
    const [classifiers, setClassifiers] = useState([]);
    const [selectedClassifiers, setSelectedClassifiers] = useState([]);
    const [knowledgeSummaries, setKnowledgeSummaries] = useState([]);
    const [selectedKnowledge, setSelectedKnowledge] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [archivedImages, setArchivedImages] = useState([]);
    const [primaryImage, setPrimaryImage] = useState(null);
    const [secondaryImage, setSecondaryImage] = useState(null);
    const [primaryImageDesc, setPrimaryImageDesc] = useState('');
    const [secondaryImageDesc, setSecondaryImageDesc] = useState('');
    const [generatedImage, setGeneratedImage] = useState(null);
    const [generatingImage, setGeneratingImage] = useState(false);

    // Load existing prompt data if provided
    useEffect(() => {
        if (existingPrompt) {
            setUserPrompt(existingPrompt.prompt);
            if (existingPrompt.metadata) {
                const { primaryImage, secondaryImage, knowledge, classifiers } = existingPrompt.metadata;
                
                // Set images
                if (primaryImage) {
                    setPrimaryImage(primaryImage);
                    setPrimaryImageDesc(primaryImage.description);
                }
                if (secondaryImage) {
                    setSecondaryImage(secondaryImage);
                    setSecondaryImageDesc(secondaryImage.description);
                }
                
                // Set knowledge and classifiers
                if (knowledge) {
                    setSelectedKnowledge(knowledge);
                }
                if (classifiers) {
                    setSelectedClassifiers(classifiers);
                }
            }
        }
    }, [existingPrompt]);

    // Fetch data when modal opens
    useEffect(() => {
        if (open) {
            setUserPrompt('');
            setSelectedClassifiers([]);
            setKnowledgeSummaries([]);
            setSelectedKnowledge([]);
            setError(null);
            fetchClassifiers();
            fetchArchivedImages();
        }
    }, [open]);

    const fetchClassifiers = async () => {
        try {
            const data = await fetchAllClassifiers();
            setClassifiers(data);
        } catch (err) {
            console.error('Error fetching classifiers:', err);
            setError('Failed to fetch classifiers');
        }
    };

    const fetchArchivedImages = async () => {
        try {
            const images = await fetchAllImages();
            setArchivedImages(images);
        } catch (err) {
            console.error('Error fetching archived images:', err);
            setError('Failed to fetch archived images');
        }
    };

    const handleClassifierChange = (event) => {
        const value = event.target.value;
        console.log('Selected classifiers:', value);
        setSelectedClassifiers(value);
    };

    // Fetch knowledge summaries when selected classifiers change
    useEffect(() => {
        const fetchKnowledgeSummaries = async () => {
            setLoading(true);
            setError(null);
            try {
                if (!selectedClassifiers || selectedClassifiers.length === 0) {
                    setKnowledgeSummaries([]);
                    return;
                }

                const classifierData = await fetchKnowledgeByClassifiers(selectedClassifiers);
                console.log('Combined knowledge data:', classifierData);
                setKnowledgeSummaries(classifierData);
            } catch (err) {
                console.error('Error fetching knowledge summaries:', err);
                setError('Failed to fetch knowledge summaries');
            } finally {
                setLoading(false);
            }
        };

        fetchKnowledgeSummaries();
    }, [selectedClassifiers]);

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            // Create the prompt with metadata
            const promptResponse = await getApi().post('/prompts', {
                prompt: userPrompt,
                primaryImage: primaryImage ? {
                    id: primaryImage.id,
                    role: 'primary',
                    description: primaryImageDesc
                } : null,
                secondaryImage: secondaryImage ? {
                    id: secondaryImage.id,
                    role: 'secondary',
                    description: secondaryImageDesc
                } : null,
                knowledge: selectedKnowledge.map(kn => ({
                    id: kn.id,
                    title: kn.title,
                    content: kn.content
                })),
                classifiers: selectedClassifiers.map(cls => ({
                    id: cls.id,
                    classText: cls.classText
                }))
            });

            console.log('Prompt created:', promptResponse.data);

            // Call Gemini middleware to generate image
            setGeneratingImage(true);
            
            // Create FormData for the request
            const formData = new FormData();
            formData.append('taskDescription', userPrompt);
            
            // Add images if they exist
            if (primaryImage) {
                const primaryImageResponse = await fetch(getImageUrl(primaryImage.filepath));
                const primaryImageBlob = await primaryImageResponse.blob();
                formData.append('images', primaryImageBlob, 'primary_image.jpg');
                formData.append('image_0_role', 'primary_subject');
                formData.append('image_0_description', primaryImageDesc);
            }
            
            if (secondaryImage) {
                const secondaryImageResponse = await fetch(getImageUrl(secondaryImage.filepath));
                const secondaryImageBlob = await secondaryImageResponse.blob();
                formData.append('images', secondaryImageBlob, 'secondary_image.jpg');
                formData.append('image_1_role', 'social_context');
                formData.append('image_1_description', secondaryImageDesc);
            }

            const response = await getApi().post('/ai/process', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            console.log('AI process response:', response.data);
            
            const { imageId, prompt: generatedPrompt } = response.data.data.generatedImage;

            // Get the generated image
            const imageResponse = await getApi().get(`/ai/image/${imageId}`, {
                responseType: 'blob'
            });

            // Create a FormData object to upload the generated image
            const uploadFormData = new FormData();
            uploadFormData.append('image', imageResponse.data, 'generated_image.png');

            // Upload the generated image to the server
            const uploadResponse = await getApi().post('/uploads', uploadFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log('Image uploaded:', uploadResponse.data);

            // Create the generated content
            const generatedContentResponse = await getApi().post('/generated', {
                promptId: promptResponse.data.id,
                content: generatedPrompt,
                imageUrl: uploadResponse.data.imageUrl
            });

            console.log('Generated content created:', generatedContentResponse.data);

            onSuccess();
            handleClose();
        } catch (err) {
            console.error('Failed to create prompt:', err);
            setError('Failed to create prompt: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
            setGeneratingImage(false);
        }
    };

    const handleClose = () => {
        // Reset all form fields
        setUserPrompt('');
        setSelectedClassifiers([]);
        setKnowledgeSummaries([]);
        setSelectedKnowledge([]);
        setPrimaryImage(null);
        setSecondaryImage(null);
        setPrimaryImageDesc('');
        setSecondaryImageDesc('');
        setError(null);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="add-prompt-modal-title"
            aria-describedby="add-prompt-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography id="add-prompt-modal-title" variant="h6" component="h2">
                        Create User Prompt
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                <Grid container spacing={2}>
                    {/* Left Column - Text and Knowledge */}
                    <Grid item xs={12} md={6}>
                        {/* User Prompt Input */}
                        <TextField
                            label="User Prompt"
                            value={userPrompt}
                            onChange={(e) => setUserPrompt(e.target.value)}
                            variant="outlined"
                            fullWidth
                            required
                            sx={{ mt: 2 }}
                        />

                        <Typography variant="h6" gutterBottom>
                            Add Knowledge (Optional)
                        </Typography>

                        {/* Classifiers Multi-select */}
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="classifiers-label">Tags (Classifiers)</InputLabel>
                            <Select
                                labelId="classifiers-label"
                                multiple
                                value={selectedClassifiers}
                                onChange={handleClassifierChange}
                                input={<OutlinedInput label="Tags (Classifiers)" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip 
                                                key={`classifier-chip-${value.id || value.classText}`} 
                                                label={value.classText} 
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {classifiers.map((classifier) => (
                                    <MenuItem 
                                        key={`classifier-${classifier.id || classifier.classText}`} 
                                        value={classifier}
                                    >
                                        {classifier.classText}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Knowledge Summaries List */}
                        {knowledgeSummaries.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Select Knowledge Titles:
                                </Typography>
                                <List>
                                    {knowledgeSummaries.map((kn) => (
                                        <ListItemButton
                                            key={`knowledge-${kn.id}`}
                                            selected={selectedKnowledge.includes(kn)}
                                            onClick={() =>
                                                setSelectedKnowledge((prev) =>
                                                    prev.includes(kn)
                                                        ? prev.filter((item) => item !== kn)
                                                        : [...prev, kn]
                                                )
                                            }
                                        >
                                            <ListItemText primary={kn.title} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Grid>

                    {/* Right Column - Images */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                            Primary Image (Required)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            {primaryImage ? (
                                <Box sx={{ position: 'relative', width: 70, height: 70 }}>
                                    <img
                                        src={getImageUrl(primaryImage.filepath)}
                                        alt={primaryImage.filename}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => setPrimaryImage(null)}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'background.paper',
                                            boxShadow: 1,
                                            '&:hover': { bgcolor: 'background.paper' }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : null}
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel>Select Primary Image</InputLabel>
                                <Select
                                    value={primaryImage || ''}
                                    onChange={(e) => setPrimaryImage(e.target.value)}
                                    label="Select Primary Image"
                                >
                                    {archivedImages.map((image) => (
                                        <MenuItem key={`primary-${image.id}`} value={image}>
                                            <img
                                                src={getImageUrl(image.filepath)}
                                                alt={image.filename}
                                                style={{ width: 70, height: 70, objectFit: 'cover' }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <TextField
                            label="Primary Image Description"
                            value={primaryImageDesc}
                            onChange={(e) => setPrimaryImageDesc(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="h6" gutterBottom>
                            Context Image (Optional)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            {secondaryImage ? (
                                <Box sx={{ position: 'relative', width: 70, height: 70 }}>
                                    <img
                                        src={getImageUrl(secondaryImage.filepath)}
                                        alt={secondaryImage.filename}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => setSecondaryImage(null)}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'background.paper',
                                            boxShadow: 1,
                                            '&:hover': { bgcolor: 'background.paper' }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : null}
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel>Select Context Image</InputLabel>
                                <Select
                                    value={secondaryImage || ''}
                                    onChange={(e) => setSecondaryImage(e.target.value)}
                                    label="Select Secondary Image"
                                >
                                    {archivedImages.map((image) => (
                                        <MenuItem key={`secondary-${image.id}`} value={image}>
                                            <img
                                                src={getImageUrl(image.filepath)}
                                                alt={image.filename}
                                                style={{ width: 70, height: 70, objectFit: 'cover' }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <TextField
                            label="Context Image Description"
                            value={secondaryImageDesc}
                            onChange={(e) => setSecondaryImageDesc(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            sx={{ mb: 2 }}
                        />
                    </Grid>
                </Grid>

                {/* Add loading state for image generation */}
                {generatingImage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        <Typography>Generating image...</Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button onClick={handleClose} sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={
                            loading ||
                            generatingImage ||
                            !userPrompt ||
                            !primaryImage
                        }
                    >
                        {loading ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddPromptModal;
